import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { Icon, useFormControl, useMultiStyleConfig } from "@chakra-ui/react";
import { useSelect } from "downshift";
import { RemoveScroll } from "react-remove-scroll";
import isEqual from "lodash/isEqual";
import { useVirtualizer } from "@tanstack/react-virtual";
import { SelectOption } from "./select-option";
import { SelectPopover, useSelectPopover } from "./select-popover";
import { SelectClearButton } from "./clear-select-button";
import { SelectEmptyState } from "./select-empty-state";
import { SelectOptions } from "./select-options";
import { SelectToggleButton } from "./select-toggle-button";
import { SelectValue } from "./select-value";
import { Spinner } from "./spinner";
import { useVirtualItems } from "./hooks/use-virtual-items";
import { SelectOptionAccessory } from "./select-option-accessory";
import { SelectOptionLabel } from "./select-option-label";
import { defaultIsOptionDisabled, defaultOptionLabel, defaultOptionValue, } from "./lib/select-helpers";
import { SelectorIcon } from "./icons";
export function MultiSelect({ isDisabled = false, isLoading = false, isInvalid = false, isClearable = false, isOptionDisabled = defaultIsOptionDisabled, emptyOptionsMessage = "No options", options, optionLabel = defaultOptionLabel, optionValue = defaultOptionValue, optionAccessory, placeholder = "", value, width = "xs", onChange, onClose, onOpen, }) {
    const formControl = useFormControl({
        isDisabled,
        isInvalid,
    });
    const popover = useSelectPopover();
    const select = useSelect({
        isOpen: popover.isOpen,
        labelId: `${formControl.id}-label`,
        toggleButtonId: formControl.id,
        items: options,
        itemToString(option) {
            return option ? optionLabel(option) : "";
        },
        stateReducer(state, { type, changes }) {
            switch (type) {
                case useSelect.stateChangeTypes.MenuKeyDownEnter:
                case useSelect.stateChangeTypes.MenuKeyDownSpaceButton:
                case useSelect.stateChangeTypes.ItemClick:
                    return {
                        ...changes,
                        isOpen: true,
                        highlightedIndex: state.highlightedIndex,
                    };
                default:
                    return changes;
            }
        },
        selectedItem: null,
        onSelectedItemChange({ selectedItem }) {
            if (!selectedItem) {
                return;
            }
            const selectedValue = optionValue(selectedItem);
            const index = value.findIndex((valueItem) => {
                return isEqual(selectedValue, valueItem);
            });
            if (index > 0) {
                onChange([...value.slice(0, index), ...value.slice(index + 1)]);
            }
            else if (index === 0) {
                onChange([...value.slice(1)]);
            }
            else {
                onChange([...value, selectedValue]);
            }
        },
        onIsOpenChange({ isOpen }) {
            if (isOpen) {
                popover.open();
                onOpen?.();
            }
            else {
                popover.close();
                onClose?.();
            }
        },
    });
    useEffect(() => {
        if (!popover.isOpen) {
            return;
        }
        const keyDown = (event) => {
            if (event.key === "a" && event.metaKey) {
                event.preventDefault();
                const allValues = options.map((option) => optionValue(option));
                onChange(allValues);
            }
        };
        document.body.addEventListener("keydown", keyDown);
        return () => {
            document.body.removeEventListener("keydown", keyDown);
        };
    }, [popover.isOpen, options, onChange]);
    const selectedOptions = useMemo(() => {
        const result = [];
        for (const valueItem of value) {
            const option = options.find((option) => {
                return isEqual(optionValue(option), valueItem);
            });
            if (option) {
                result.push(option);
            }
        }
        return result;
    }, [options, optionLabel, value]);
    const showClearButton = isClearable && value.length > 0;
    const clear = useCallback(() => {
        select.reset();
        onChange([]);
    }, [onChange]);
    const optionsContainer = useRef(null);
    const { virtualItems, getVirtualItemKey, getVirtualItemIndex } = useVirtualItems({
        options,
        optionLabel,
    });
    const virtualizer = useVirtualizer({
        count: virtualItems.length,
        getItemKey: getVirtualItemKey,
        getScrollElement() {
            return optionsContainer.current;
        },
        estimateSize() {
            return 32;
        },
    });
    useEffect(() => {
        const virtualIndex = getVirtualItemIndex(select.highlightedIndex);
        if (virtualIndex >= 0) {
            virtualizer.scrollToIndex(virtualIndex);
        }
    }, [select.highlightedIndex]);
    useEffect(() => {
        if (!popover.isOpen && select.highlightedIndex === -1) {
            virtualizer.scrollToOffset(0);
        }
    }, [popover.isOpen, select.highlightedIndex]);
    const styles = useMultiStyleConfig("Select");
    return (React.createElement(RemoveScroll, { enabled: popover.isOpen },
        React.createElement(SelectToggleButton, { "aria-invalid": formControl["aria-invalid"], "aria-required": formControl["aria-required"], "aria-describedby": formControl["aria-describedby"], "data-selected": value.length > 0 ? "true" : undefined, width: width, disabled: formControl.disabled, "data-testid": "multi-select", ...select.getToggleButtonProps(popover.getTargetProps({
                onFocus: formControl.onFocus,
                onBlur: formControl.onBlur,
            })) },
            React.createElement(SelectValue, null, value.length > 0
                ? selectedOptions.map((option) => optionLabel(option)).join(", ")
                : placeholder),
            isLoading && React.createElement(Spinner, { size: "sm" }),
            !isLoading &&
                (showClearButton ? (React.createElement(SelectClearButton, { disabled: formControl.disabled, onClick: clear })) : (React.createElement(Icon, { as: SelectorIcon, __css: styles["icon"], color: "text.secondary" })))),
        React.createElement(SelectPopover, { isOpen: popover.isOpen, "data-testid": "multi-select-popover", ...select.getMenuProps({
                ...popover.getPopoverProps(),
                onKeyDown(event) {
                    if (event.key === "Escape") {
                        event.stopPropagation();
                    }
                },
            }) },
            virtualItems.length === 0 && (React.createElement(SelectEmptyState, null, emptyOptionsMessage)),
            virtualItems.length > 0 && (React.createElement(SelectOptions, { ref: optionsContainer, virtualizer: virtualizer, virtualItems: virtualItems }, (virtualItem, index) => {
                if (virtualItem.type === "option") {
                    const accessory = optionAccessory?.(virtualItem.option);
                    return (React.createElement(SelectOption, { isSelected: selectedOptions.includes(virtualItem.option), "data-index": index, ...select.getItemProps({
                            ref: virtualizer.measureElement,
                            item: virtualItem.option,
                            index: virtualItem.index,
                            disabled: isOptionDisabled(virtualItem.option),
                        }) },
                        accessory && (React.createElement(SelectOptionAccessory, { accessory: accessory })),
                        React.createElement(SelectOptionLabel, null, optionLabel(virtualItem.option))));
                }
                return null;
            })))));
}
